<!--
  - Copyright (c) 2021 by Leyton Group - All Rights Reserved.
  -  This source code, and resulting software, is the confidential and proprietary information
  -  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  -  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  -  and shall use it only in accordance with the terms and conditions of any and all license
  -  agreements you have entered into with The Company.
  -->

<template>
  <div id="SimulationsList" v-if="$route.name === 'Simulations'">
    <!-- ACTIONS -->
    <actions
      :view="view"
      v-on:changeView="changeView($event)"
      :isSimulationContext="true"
      :queryConfig="queryConfig"
      @applyFilter="applyFilter($event)"
      @reloadData="applyFilter($event)"
      @download="download($event)"
      @search="simulationSearch"
    ></actions>

    <page-loader v-if="loading" :hasActions="true"></page-loader>

    <!-- GRID AND VIEW SIMULATIONS -->
    <div class="giga-simulations_content">
      <b-tabs v-if="!loading" @activate-tab="onTabChange($event)">
        <b-tab
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :title="tab.title"
          :active="activeTab === index"
        >
          <!-- GRID VIEW -->
          <div v-if="view == 'grid' && !loading">
            <simulation-grid
              style="margin-top: 20px;"
              :simulations="getSimulations.content"
            ></simulation-grid>
          </div>

          <!-- LIST VIEW -->
          <table
            v-if="
              view == 'list' && !loading && getSimulations.content.length != 0
            "
            class="giga-table"
            :aria-describedby="$t('all_simulations')"
          >
            <thead>
              <tr>
                <th
                  @click="sortTable('name')"
                  class="sort"
                  scope="col"
                  :class="sortBy == 'name' ? sortDir : ''"
                >
                  {{ $t("simulation_name") }}
                </th>
                <th
                  @click="sortTable('estimated_end_date')"
                  class="sort"
                  scope="col"
                  :class="sortBy == 'estimated_end_date' ? sortDir : ''"
                >
                  {{ $t("estimated_end_date") }}
                </th>
                <th scope="col">
                  {{ $t("Vol. théorique Classique") }}
                </th>
                <th scope="col">
                  {{ $t("Vol. théorique Précaire") }}
                </th>
                <th scope="col">
                  {{ $t("Valorisation économique") }}
                </th>
                <th
                  @click="sortTable('status')"
                  class="sort simulations-status"
                  scope="col"
                  :class="sortBy == 'status' ? sortDir : ''"
                >
                  {{ $t("status") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="simulation in getSimulations.content">
                <tr
                  :id="`sim-${simulation.name}`"
                  class="cursor-pointer"
                  @click="onRowSelected(simulation.id)"
                  :key="simulation.id"
                >
                  <td
                    class="simulation-name-column"
                    :class="{ 'is-draft': simulation.isDraft }"
                  >
                    {{ simulation.name }}
                    <span
                      class="tooltip-container"
                      v-if="
                        (simulation.cofracState === 'BOTH' ||
                          simulation.cofracState === 'ONLY_COFRAC') &&
                          !simulation.isDraft &&
                          simulation.status !== 'CANCEL' &&
                          (simulation.status === 'NEW' ||
                            simulation.status === 'CARRIED_OUT' ||
                            simulation.status === 'TO_BE_TRANSFORMED')
                      "
                    >
                      <svg
                        class="warning-icon"
                        viewBox="0 0 15 15"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          class="triangle"
                          d="M7.5 1.2
                             C7 1.2 6.7 1.5 6.5 1.8
                             L0.8 11.2
                             A1.5 1.5 0 0 0 2.2 13
                             H12.8
                             A1.5 1.5 0 0 0 14.2 11.2
                             L8.5 1.8
                             C8.3 1.5 8 1.2 7.5 1.2Z"
                        />
                        <path
                          class="exclamation-mark"
                          d="M7.5 3.5C7.75 3.5 8 3.75 8 4L7.8 8C7.8 8.25 7.6 8.5 7.5 8.5C7.4 8.5 7.2 8.25 7.2 8L7 4C7 3.75 7.25 3.5 7.5 3.5Z"
                        />
                        <circle
                          class="exclamation-mark"
                          cx="7.5"
                          cy="10.5"
                          r="0.8"
                        />
                      </svg>
                      <span class="tooltip-text">{{
                        $t("quality_control_required")
                      }}</span>
                    </span>
                    <span
                      class="tooltip-container"
                      v-if="
                        simulation.isRgeInstaller &&
                          !simulation.isDraft &&
                          simulation.status !== 'CANCEL' &&
                          (simulation.status === 'NEW' ||
                            simulation.status === 'CARRIED_OUT' ||
                            simulation.status === 'TO_BE_TRANSFORMED')
                      "
                    >
                      <img
                        src="@/assets/images/rge.png"
                        alt="RGE"
                        class="rge-icon"
                      />
                      <span class="tooltip-text">{{
                        $t("rge_installer_required")
                      }}</span>
                    </span>
                    <span
                      v-if="
                        simulation.isDraft && simulation.status !== 'CANCEL'
                      "
                      class="draft-badge-list"
                      >Brouillon</span
                    >
                    <span
                      v-if="simulation.status === 'CANCEL'"
                      class="perdu-badge-list"
                      >Perdu</span
                    >
                  </td>
                  <td :class="{ 'is-draft': simulation.isDraft }">
                    {{
                      simulation.deliveryDate > 0
                        ? moment(simulation.deliveryDate, "x")
                            .locale("fr")
                            .format("DD MMMM YYYY")
                        : moment()
                            .locale("fr")
                            .format("DD MMMM YYYY")
                    }}
                  </td>
                  <td>
                    <p :class="{ 'is-draft': simulation.isDraft }">
                      {{ simulation.expectedThClassicVolume | formatNumber }}
                      kWh cumac
                    </p>
                  </td>
                  <td :class="{ 'is-draft': simulation.isDraft }">
                    <p :class="{ 'is-draft': simulation.isDraft }">
                      {{ simulation.expectedThPrecarityVolume | formatNumber }}
                      kWh cumac
                    </p>
                  </td>
                  <td :class="{ 'is-draft': simulation.isDraft }">
                    <p :class="{ 'is-draft': simulation.isDraft }">
                      € {{ simulation.economicValorisation | formatNumber }}
                    </p>
                  </td>
                  <td class="simulations-status">
                    <b-badge
                      class="giga-badge"
                      :class="[
                        { inProgress: simulation.status == 'NEW' },
                        { delivered: simulation.status == 'CARRIED_OUT' },
                        { perdu: simulation.status == 'CANCEL' },
                        { draft: simulation.isDraft }
                      ]"
                    >
                      {{
                        simulation.isDraft
                          ? "Brouillon"
                          : $t("simulation_status." + simulation.status)
                      }}
                    </b-badge>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <!-- EMPTY STATE -->
          <div v-if="!getSimulations.content.length">
            <empty-state
              v-if="
                !search.length && !customFilter.rules.length && activeTab === 0
              "
              module="simulations"
              title="Commencer à estimer votre projet."
              description="Vous n'avez aucune simulation."
              :addSimulation="true"
            ></empty-state>
            <empty-state
              v-else
              module="simulations"
              title="Aucun élément trouvé pour l’instant!"
              description="Cette liste ne contient aucun élément."
              :addSimulation="false"
            ></empty-state>
          </div>
        </b-tab>
      </b-tabs>

      <!-- LIST VIEW -->

      <!-- PAGINATION -->
      <pagination
        :length="length"
        :rows="rows"
        :perPage="perPage"
        :currentPage="currentPage"
        v-on:changePerPage="changePerPage($event)"
        v-on:changeCurrentPage="changeCurrentPage($event)"
      ></pagination>

      <!-- CONFIRM VALIDATION MODAL -->
      <confirm-modal
        id="confirm-validation"
        :content="$t('validate-simulation-message')"
        :modal_title="$t('Confirm-validation')"
        :loader="false"
        fontSize="h3"
        backdrop-close="false"
        @yesClick="validate(false, 'confirm-validation')"
        @close="closeConfirmModal('confirm-validation')"
      ></confirm-modal>

      <!-- CONFIRM CLOSE MODAL -->
      <confirm-modal
        id="confirm-lose"
        :content="$t('cancel-simulation-message')"
        :modal_title="$t('Confirm-lose')"
        :loader="false"
        fontSize="h3"
        backdrop-close="false"
        @yesClick="validate(true, 'confirm-lose')"
        @close="closeConfirmModal('confirm-lose')"
      ></confirm-modal>

      <!--DELETE SIMULATION REASON MODAL -->
      <delete-simulation-reason-modal
        id="delete-simulation-reason"
        :modal-title="$t('reason_for_loss')"
        :content="$t('delete-simulation-message')"
        :loader="false"
        fontSize="h3"
        @reasonSubmitted="openFinalConfirmModal"
        @cancel="onCancelDelete"
        :reasonData="reasonLoseData"
      ></delete-simulation-reason-modal>
    </div>
  </div>
  <router-view v-else />
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Actions from "../../components/static/Actions.vue";
import Pagination from "../../components/static/Pagination.vue";
import SimulationGrid from "../../components/static/SimulationGrid";
import ConfirmModal from "../../components/static/ConfirmModal";
import { queryConstant } from "../../shared/queryConstant";
import { simulationService } from "./services";
import { routeUtils } from "@/shared/route.util";
import { constants } from "@/shared/constants";
import PageLoader from "../../components/static/pageLoader.vue";
import { debounce, cloneDeep } from "lodash";
import EmptyState from "../../components/static/emptyState.vue";
import { helpers } from "@/shared/helper";
import DeleteSimulationReasonModal from "../../components/static/DeleteSimulationReasonModal.vue";

export default {
  name: "Simulations",
  components: {
    ConfirmModal,
    Actions,
    Pagination,
    SimulationGrid,
    PageLoader,
    EmptyState,
    DeleteSimulationReasonModal
  },
  data() {
    const _pageSize_10 = 10;
    const _pageSize_25 = 25;
    const _pageSize_50 = 50;
    return {
      view: "grid",
      scrollBarSettings: {
        scrollXMarginOffset: 50
      },
      perPage: _pageSize_10,
      activeTab: 0,
      currentPage: 1,
      length: [_pageSize_10, _pageSize_25, _pageSize_50],
      sortBy: "id",
      sortDir: true,
      customFilter: queryConstant.setDefaultCustomFilter(),
      queryConfig: queryConstant.SIMULATION_CONFIG,
      canValidateSimulation: false,
      search: "",
      fetchData: () => {},
      USER_TYPE: constants.USER_TYPE,
      ROLE_TYPE: constants.ROLE_TYPE,
      tabs: [
        { key: "all", title: "Tous" },
        { key: "in-progress", title: "En cours" },
        { key: "draft", title: "Brouillons" },
        { key: "cancel", title: "Perdus" }
      ],
      reasonLoseData: {
        reason: "",
        message: ""
      },
      showDeleteReasonAgain: false,
      selectedSimulationIdForDelete: null
    };
  },
  mounted() {
    this.fetchData = debounce(this.fetchSimulations, 500);
    this.fetchSimulations(this.tab);
    this.canValidateSimulation = routeUtils.isAllowed(
      constants.VALIDATE_SIMULATION
    );
    if (!this.sidebarShowStatus) {
      this.setSidebarShow(true);
      this.toggleSidebar();
    }
    const otherQueryConfig = cloneDeep(queryConstant.SIMULATION_CONFIG);
    if (
      this.currentUser.clientType === this.USER_TYPE.B2B2B &&
      this.currentUser.admin
    ) {
      otherQueryConfig.fields.push(
        queryConstant.AGENCY_FILTER_OPTION,
        queryConstant.USER_FILTER_OPTION
      );
    } else if (
      this.currentUser.clientType === this.USER_TYPE.B2B2B &&
      this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE
    ) {
      otherQueryConfig.fields.push(
        queryConstant.AGENCY_FILTER_OPTION,
        queryConstant.USER_FILTER_OPTION
      );
    } else if (this.isNotB2B2B && this.currentUser.admin) {
      otherQueryConfig.fields.push(queryConstant.USER_FILTER_OPTION);
    }
    this.queryConfig = otherQueryConfig;
  },
  computed: {
    ...mapGetters("account", { currentUser: "getCurrentUser" }),
    ...mapGetters("user", ["getUsers"]),
    ...mapGetters("simulation", ["getSimulations", "getSelectedSimulationId"]),
    ...mapGetters("loader", { loading: "getLoading" }),
    ...mapGetters("leftsidebar", { sidebarShowStatus: "getSidebarShowStatus" }),
    rows() {
      return this.getSimulations.totalElements;
    },
    isAnyOfUserTypes() {
      return helpers.isAnyOfUserTypes();
    },
    isNotB2B2B() {
      return helpers.isNotB2B2B();
    },
    tab() {
      return this.tabs[this.activeTab].key;
    }
  },
  methods: {
    ...mapActions("simulation", [
      "getSimulationsAction",
      "validateOrLoseSimulationAction",
      "selectedSimulationIdAction",
      "getSimulationsAdminAgence",
      "getSimulationsChargeDeProjet",
      "getSimulationsChargeDeProjetById"
    ]),
    ...mapMutations({
      toggleSidebar: "leftsidebar/toggleSidebar",
      setSidebarShow: "leftsidebar/setSidebarShow"
    }),
    changePerPage(perPage) {
      this.currentPage = 1;
      this.perPage = perPage;
      this.fetchSimulations(this.tab);
    },
    changeCurrentPage(currentPage) {
      this.currentPage = currentPage;
      this.fetchSimulations(this.tab);
    },
    changeView(view) {
      this.view = view;
    },
    sortTable(name) {
      if (name === this.sortBy) {
        this.sortDir = !this.sortDir;
      } else {
        this.sortDir = false;
      }
      this.sortBy = name;
      this.fetchSimulations(this.tab);
    },
    onRowSelected(id) {
      this.$router.push({ name: "SimulationDetail", params: { id: id } });
    },
    validate(lose, modal) {
      const reason = this.reasonLoseData.reason || "";
      const message = this.reasonLoseData.message || "";

      this.validateOrLoseSimulationAction({
        id: this.getSelectedSimulationId,
        lose: lose,
        reason,
        message
      }).then(
        () => {
          this.fetchSimulations(this.tab);
          this.reasonLoseData = { reason: "", message: "" };
          this.selectedSimulationIdForDelete = null;
        },
        error => {
          console.log(error);
        }
      );
      this.$root.$emit("bv::hide::modal", modal);
    },
    openFinalConfirmModal(data) {
      this.reasonLoseData = data;
      this.showDeleteReasonAgain = true;
      this.$root.$emit("bv::show::modal", "confirm-lose");
    },
    onCancelDelete() {
      if (!this.showDeleteReasonAgain) {
        this.reasonLoseData = { reason: "", message: "" };
      }
      this.selectedSimulationIdForDelete = null;
      this.$root.$emit("bv::show::modal", "delete-simulation-reason");
    },
    onTabChange(index) {
      this.activeTab = index;
      this.fetchSimulations(this.tab);
    },
    closeConfirmModal(modal) {
      this.$root.$emit("bv::hide::modal", modal);
      if (modal === "confirm-lose" && this.showDeleteReasonAgain) {
        this.showDeleteReasonAgain = false;
        this.$nextTick(() => {
          this.$root.$emit("bv::show::modal", "delete-simulation-reason");
        });
      } else {
        this.reasonLoseData = { reason: "", message: "" }; // reset si pas retour
      }
    },
    applyFilter(event) {
      this.customFilter = event.filter.custom;
      this.fetchSimulations(this.tab);
    },
    download() {
      simulationService.downloadSimulations(this.customFilter);
    },
    simulationSearch(search) {
      this.search = search;
      this.fetchData();
    },
    fetchSimulations(tab = "all") {
      if (
        this.currentUser.clientType === this.USER_TYPE.B2B2B &&
        this.customFilter.rules.some(rule => rule.field === "agence") &&
        (this.currentUser.admin ||
          this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
      ) {
        const agence = this.customFilter.rules.find(
          rule => rule.field === "agence"
        );
        let customFilter = cloneDeep(this.customFilter);
        customFilter.rules = this.customFilter.rules.filter(
          rule => rule.field !== "agence"
        );
        this.getSimulationsAdminAgence({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: customFilter,
          search: this.search,
          agenceId: agence?.rules[0]?.value?.id,
          tab: this.tab
        });
      } else if (
        this.isAnyOfUserTypes &&
        this.customFilter.rules.some(rule => rule.field === "user")
      ) {
        const user = this.customFilter.rules.find(
          rule => rule.field === "user"
        );
        let customFilter = cloneDeep(this.customFilter);
        customFilter.rules = this.customFilter.rules.filter(
          rule => rule.field !== "user"
        );
        this.getSimulationsChargeDeProjetById({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: customFilter,
          search: this.search,
          userId: user?.rules[0]?.value?.id,
          tab: this.tab
        });
      } else if (
        (this.currentUser.clientType === this.USER_TYPE.B2B2B &&
          this.currentUser.role?.roleType === this.ROLE_TYPE.USER) ||
        (this.isNotB2B2B &&
          this.currentUser.role?.roleType === this.ROLE_TYPE.USER)
      ) {
        this.getSimulationsChargeDeProjet({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: this.customFilter,
          search: this.search,
          tab: this.tab
        });
      } else {
        this.getSimulationsAction({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: this.customFilter,
          search: this.search,
          tab: this.tab
        });
      }
    }
  }
};
</script>

<style lang="scss">
.view-area {
  position: relative;
  min-height: 500px;
  height: calc(100vh - 420px);
  padding-right: 1rem;
  margin-right: -1rem;
  overflow-y: auto;
}
.view-area-table {
  position: relative;
  min-height: 430px;
  height: calc(100vh - 470px);
}
.btn-with-radius {
  border-radius: 6px;
}

.draft-badge-list {
  border-radius: 30px;
  border-style: solid;
  border-width: 1px;
  background-image: linear-gradient(to bottom, #e0e7ee, #e0e7ee),
    linear-gradient(to bottom, #cdd8e3, #eef3f9);
  background-origin: border-box;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #6b829a;
  border-color: #cdd8e3;
  padding: 4px 8px;
  margin-left: 7px;
}
.perdu-badge-list {
  padding: 4px 8px;
  border-radius: 30px;
  border-style: solid;
  border-width: 1px;
  background-image: linear-gradient(to bottom, #ffe9e4, #ffe9e4),
    linear-gradient(to bottom, #ffc6be, #ffdbd9);
  background-origin: border-box;
  margin-bottom: 6px;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #cd1a02;
  border-color: #ffc6be;
  margin-left: 7px;
}
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  width: auto;
  white-space: nowrap;
  background: #fffc9d;
  color: black;
  text-align: center;
  padding: 6px;
  border-radius: 6px;
  position: absolute;
  z-index: 10;
  top: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  font-size: 12px;
}
.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
.warning-icon,
.rge-icon {
  width: 22px;
  height: 22px;
  margin-left: 6px;
  vertical-align: middle;
  cursor: pointer;
}
.triangle {
  fill: #fffc9d;
  stroke: black;
  stroke-width: 1px;
  stroke-linejoin: round;
}
.exclamation-mark {
  fill: black;
}
</style>
